import * as THREE from 'three';
import { DRUID_PATH } from '@somnhold/content';
import { hexToWorld, leafLayout, pastLayout, underLayout, type BattleState, type LeafLayout, type MapDef, type PastLayout, type UnderLayout } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import { lookPoint, Precip, type OmenCtx } from '../omens/common.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome, smoothTiles } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The autumn wood over Los Hornos (docs/sendas/druida.md, chapter 3), for the druid's path: a red and ochre wood,
 * leaves to the ankles, a low sun through an afternoon mist (cool grey, so the reds stand out), leaves falling. Each
 * place:
 *
 * - `autumnWood` (act 1), `mapleFoot` (Ruda's story), `autumnCine` (the cinematic): the wood, Ceda's way worn dark
 *   through the litter, Olmo's maple at the top (a great old maple, red, its roots flaring over the ground, the third
 *   face carved in it), Los Hornos below in the cinematic. A stalker under the leaves shows as a hump of leaves going
 *   and the furrow it leaves; heaving by Ceda, a red ring closing where it comes up; a weaver's silk, a pale line to
 *   Ceda while it spins.
 * - `autumnPast` (the memory, act 2), `mapleCarve` (its end): the same wood a hundred years back, younger and thinner,
 *   the maple young, six houses of Los Hornos at the foot; three roots coming down the slope over the ground, forking;
 *   the rot black along them, its heads pulsing pale; a cut root a pale fresh end with the druid's root driven in; the
 *   root where the next comes down ringed before it does. (The canvas greys it: what matters reads by light and dark.)
 * - `mapleRoots` (act 3, the test), `mapleFace` (the close): the clearing by the maple; the knot, a black burl, where
 *   the rot gets in; the roots in sight (rotten ones black and glossy, sound ones brown with Olmo's notch); on every
 *   cell with roots under it its mushrooms, big, one per root, black over the rot and white over sound wood; the rot's
 *   stain spreading on the ground along its roots; the cuts; the maple's three great boughs, one falling for each
 *   branch lost; the broodmother's leap ringed; the rot going out, its mushrooms turning white one by one.
 * - `hornosTown` (the opening): a street of Los Hornos at dusk, houses of stone with kilns smoking, cracks in the earth,
 *   the trees above black at their tips. `rudaYard` (the campfire): Ruda's yard at night, the kilns alight.
 *
 * Draw calls: the dressing (one merged mesh), the furrows' leaves (one instanced), the roots (one instanced), the heads
 * and the knot (one instanced), the mushrooms (two instanced: stems, caps), the stain (one instanced), the cut marks
 * (one instanced), the rings (one), the silk lines (two), the boughs (three), the falling leaves (two), the glow layer,
 * the sky dome. No lights of its own: the board's are repainted (HOURS).
 */

export const OTONO_PLACES = new Set(['autumnWood', 'mapleFoot', 'autumnCine', 'hornosTown', 'autumnPast', 'mapleCarve', 'rudaYard', 'mapleRoots', 'mapleFace', 'deepNet']);

/**
 * The druid's chapter 5, act 3 (`deepNet`, «Lo que hay abajo»): the same net and its rule, inside the Grandsire round
 * the Carcoma's chamber. The maple's place is the chamber's mouth (an arch of black roots, a pale glow breathing in
 * it); the wood's trees are columns of living wood with veins of sap; the floor dark heartwood; no leaves falling; the
 * light amber through the bark.
 */
const DEEP_LOOK = { bg: '#3a2414', fog: ['#4a2e1a', 28, 110] as [string, number, number], hemi: ['#ffd8a8', '#3a2414', 2.3] as [string, string, number], key: ['#ffc888', 2.2] as [string, number], rim: ['#9ad08a', 0.9] as [string, number], sky: ['#3a2414', '#5a3820', '#ffae3c'] as [string, string, string] };
const HEARTWOOD = ['#5a3c24', '#64442a', '#4e341e', '#6a4a2e'];

const S = WORLD_PER_HEX;
const K = S / 1000;
const RED = ['#a8301e', '#c0402a', '#8e2a1c', '#cc5430', '#b4381e'];
const OCHRE = ['#b06424', '#a2561e', '#c27030', '#8e4a1c'];
const BARK = ['#4a3428', '#3e2c22', '#56402e'];
const LITTER = ['#7a3420', '#86401e', '#6e2e1c', '#944a24', '#6a3a22'];
const EARTH = ['#6a5a4c', '#62544a', '#72604e'];
const STONE = ['#646670', '#5a5c66', '#6e7078'];
const ROT = new THREE.Color('#141016');
const ROT_GLOW = new THREE.Color('#9a7aa8');
const SOUND = new THREE.Color('#7a5a3c');
const CUT = new THREE.Color('#d8e8c0');
const SPENT = new THREE.Color('#3a3634');
const WHITE = new THREE.Color('#f2f4f6');
const BLACK = new THREE.Color('#18161c');
const DRUID = new THREE.Color('#8ed060');
const WARN = new THREE.Color('#ff4f3a');
const WARN2 = new THREE.Color('#ff8a6e');

type Hour = 'day' | 'dusk' | 'night' | 'dawn';

/** The light by the hour: a low autumn sun through mist (warm key, cool mist), dusk red, night by the kilns. */
const HOURS: Record<Hour, { bg: string; fog: [string, number, number]; hemi: [string, string, number]; key: [string, number]; rim: [string, number]; sky: [string, string, string] }> = {
  day: { bg: '#8e9098', fog: ['#9a98a0', 26, 105], hemi: ['#eef0f6', '#3a2a22', 2.1], key: ['#ffc898', 2.6], rim: ['#a8bce8', 1.3], sky: ['#5a6e90', '#b0a8a8', '#ffc890'] },
  dawn: { bg: '#9a9298', fog: ['#a49a9e', 24, 100], hemi: ['#ffe8e0', '#3a2a24', 2.2], key: ['#ffc0a0', 2.5], rim: ['#b0c0ee', 1.3], sky: ['#5a6488', '#c0a4a0', '#ffc8a8'] },
  dusk: { bg: '#4e4050', fog: ['#6a5862', 20, 92], hemi: ['#ffd4c0', '#2a1c1c', 2.1], key: ['#ff9a5a', 2.4], rim: ['#8a96d8', 1.6], sky: ['#242040', '#8c4a4c', '#ffa060'] },
  night: { bg: '#0c0a10', fog: ['#1a1418', 16, 66], hemi: ['#9290b0', '#100a0a', 1.35], key: ['#b8b0dc', 0.85], rim: ['#d0703a', 1.3], sky: ['#04040a', '#1e1418', '#a8482a'] },
};

export class OtonoLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(260);
  private dome: SkyDome | null = null;
  private leavesA: Precip | null = null;
  private leavesB: Precip | null = null;
  private terrain: TerrainBuild | null = null;
  private now = 0;
  private look = new THREE.Vector3();
  private lights: { p: THREE.Vector3; c: THREE.Color; size: number; a: number; seed: number; flick: number }[] = [];
  private lastState: BattleState | null = null;
  private m4 = new THREE.Matrix4();
  private q4 = new THREE.Quaternion();
  private v3 = new THREE.Vector3();
  private v4 = new THREE.Vector3();
  private s3 = new THREE.Vector3();
  private c3 = new THREE.Color();
  private e3 = new THREE.Euler();
  private up = new THREE.Vector3(0, 1, 0);
  private hour: Hour = 'day';
  // Shared.
  private rings: THREE.InstancedMesh | null = null;
  private silk: THREE.Mesh[] = [];
  // Act 1.
  private Ll: LeafLayout | null = null;
  private bits: THREE.InstancedMesh | null = null;
  private trail = new Map<number, THREE.Vector3[]>();
  private mapleAt: THREE.Vector3 | null = null;
  // Act 2.
  private Pl: PastLayout | null = null;
  private roots: THREE.InstancedMesh | null = null;
  private rootKey = '';
  private nodeAt: THREE.Vector3[] = [];
  private blobs: THREE.InstancedMesh | null = null;
  private marks: THREE.InstancedMesh | null = null;
  // Act 3.
  private Ul: UnderLayout | null = null;
  private deep = false;
  private cellAt: THREE.Vector3[] = [];
  private stems: THREE.InstancedMesh | null = null;
  private caps: THREE.InstancedMesh | null = null;
  private capOf: number[] = [];
  private capBlack: boolean[] = [];
  private capKey = '';
  private stain: THREE.InstancedMesh | null = null;
  private stainKey = '';
  private visRoots: THREE.InstancedMesh | null = null;
  private visKey = '';
  private visOf: { cell: number; strand: number }[] = [];
  private boughs: THREE.Group[] = [];
  private fall: number[] = [0, 0, 0];
  private knotAt: THREE.Vector3 | null = null;
  private outT = -1;
  private seedFlash = 0;

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `otono-${place}`;
    this.deep = place === 'deepNet';
    const kit = new Kit();
    this.shared();
    if (place === 'autumnWood' || place === 'mapleFoot' || place === 'autumnCine') this.buildWood(kit, s, place === 'autumnCine');
    else if (place === 'autumnPast' || place === 'mapleCarve') this.buildPast(kit, s, place === 'mapleCarve');
    else if (place === 'mapleRoots' || place === 'mapleFace' || place === 'deepNet') this.buildRoots(kit, s, place === 'mapleFace');
    else this.buildTown(kit, place === 'rudaYard');
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.88, metalness: 0.02, flatShading: true });
    this.mats.push(mat);
    const dress = kit.build(mat, 'otono-dress');
    if (dress) {
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    // Leaves falling, red and ochre, slow and swaying (none inside the Grandsire).
    const n = this.deep ? 0 : ctx.low ? 50 : 110;
    this.leavesA = new Precip({ count: n, box: [14, 8, 14], fall: 0.55, wind: [0.35, 0.15], sway: 0.9, size: [0.08, 0.06], color: '#c4402a', opacity: 0.92, round: true }, 61);
    this.leavesB = new Precip({ count: n, box: [14, 8, 14], fall: 0.48, wind: [0.3, 0.2], sway: 1.1, size: [0.075, 0.055], color: '#c06a28', opacity: 0.9, round: true }, 62);
    this.group.add(this.leavesA.mesh, this.leavesB.mesh);
    this.group.add(this.glow.mesh);
  }

  /* ---------- helpers ---------- */

  private track<T extends THREE.Material | THREE.BufferGeometry>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else this.geos.push(x);
    return x;
  }

  private hex(q: number, r: number, h = 0): THREE.Vector3 {
    const c = hexToWorld(q, r);
    return this.ctx.at(c.x, c.y, h);
  }

  private light(p: THREE.Vector3, color: THREE.ColorRepresentation, size: number, a: number, flick = 0): void {
    this.lights.push({ p: p.clone(), c: new THREE.Color(color), size, a, seed: this.lights.length * 1.37, flick });
  }

  private vcMat: THREE.MeshStandardMaterial | null = null;
  private vc(): THREE.MeshStandardMaterial {
    if (!this.vcMat) this.vcMat = this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0.02, flatShading: true }));
    return this.vcMat;
  }

  /** A small piece built once from the kit's parts (its vertex colours), to be instanced. */
  private merged(fill: (k: Kit) => void): THREE.BufferGeometry {
    const k = new Kit();
    fill(k);
    const m = k.build(this.vc(), 'otono-piece');
    return this.track((m as THREE.Mesh).geometry);
  }

  /** An instanced mesh of `n` of a geometry, its colours its own. */
  private inst(geo: THREE.BufferGeometry, mat: THREE.Material, n: number, name: string, order = 0): THREE.InstancedMesh {
    const m = new THREE.InstancedMesh(geo, mat, n);
    m.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(n * 3), 3);
    m.count = 0;
    m.frustumCulled = false;
    m.name = name;
    if (order) m.renderOrder = order;
    this.group.add(m);
    return m;
  }

  private shared(): void {
    const rg = this.track(new THREE.RingGeometry(0.86, 1, 40).rotateX(-Math.PI / 2));
    const rm = this.track(new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.9, depthWrite: false, side: THREE.DoubleSide }));
    this.rings = this.inst(rg, rm, 24, 'otono-rings', 5);
    // Silk: a pale band from a weaver to Ceda, laid along the ground.
    const sg = this.track(new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2).translate(0, 0, 0.5));
    const sm = this.track(new THREE.MeshBasicMaterial({ color: '#eef2f6', transparent: true, opacity: 0.75, depthWrite: false, side: THREE.DoubleSide }));
    for (let i = 0; i < 3; i++) {
      const m = new THREE.Mesh(sg, sm);
      m.visible = false;
      m.renderOrder = 6;
      m.name = 'otono-silk';
      this.silk.push(m);
      this.group.add(m);
    }
  }

  /** An autumn tree: a dark trunk, a crown in lumps of red and ochre (`bare`: a few leaves left on its boughs). */
  private tree(kit: Kit, p: THREE.Vector3, k: number, tall = 1, bare = false): void {
    const cyl = new THREE.CylinderGeometry(0.1, 0.17, 1, 7);
    const ico = new THREE.IcosahedronGeometry(0.5, 0);
    const h = (2.3 + k * 1.7) * tall;
    const lean = (k - 0.5) * 0.14;
    kit.add(cyl, BARK[Math.floor(k * 7) % 3] as string, place(p.x, p.y + h * 0.5, p.z, k * 6, new THREE.Vector3(1.1, h, 1.1), lean, -lean));
    // Boughs out from high on the trunk.
    for (let i = 0; i < 3; i++) {
      const a = i * 2.1 + k * 4;
      kit.add(cyl, BARK[(i + 1) % 3] as string, place(p.x + Math.cos(a) * 0.3, p.y + h * 0.78, p.z + Math.sin(a) * 0.3, a, new THREE.Vector3(0.45, h * 0.32, 0.45), Math.cos(a) * 0.9, -Math.sin(a) * 0.9));
    }
    const top = p.y + h;
    const pal = k > 0.55 ? RED : OCHRE;
    const n = bare ? 2 : 6;
    for (let i = 0; i < n; i++) {
      const a = i * 1.6 + k * 4;
      const r = 0.5 + (i % 3) * 0.22;
      kit.add(ico, pal[(i + Math.floor(k * 5)) % pal.length] as string, place(p.x + lean * h + Math.cos(a) * r * 0.62, top - 0.35 + (i % 3) * 0.32, p.z + Math.sin(a) * r * 0.62, a, (0.95 + (i % 2) * 0.4) * (0.85 + k * 0.4) * (bare ? 0.6 : 1)));
    }
    // The leaves it shed, round its foot.
    this.litter(kit, p, 6, Math.floor(k * 97), pal);
    cyl.dispose();
    ico.dispose();
  }

  /** Fallen leaves about a point: flat bits, red and ochre (merged). */
  private litter(kit: Kit, p: THREE.Vector3, n: number, seed: number, pal = LITTER): void {
    const ico = new THREE.IcosahedronGeometry(0.5, 0);
    for (let i = 0; i < n; i++) {
      const a = seed * 2.3 + i * 2.39;
      const d = 0.3 + ((i * 37 + seed * 11) % 10) * 0.09;
      kit.add(ico, pal[(i + seed) % pal.length] as string, place(p.x + Math.cos(a) * d, p.y + 0.02, p.z + Math.sin(a) * d, a, new THREE.Vector3(0.36, 0.05, 0.26)));
    }
    ico.dispose();
  }

  /** Olmo's face carved in bark, looking south: the oval smoothed pale, the eyes, the mouth; the words under it. */
  private face(kit: Kit, p: THREE.Vector3, size: number, fresh: boolean): void {
    const sph = new THREE.SphereGeometry(0.42, 12, 8);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const base = fresh ? '#b89a7c' : '#7a604c';
    const cut = '#2a1a12';
    kit.add(sph, base, place(p.x, p.y, p.z, 0, new THREE.Vector3(1.4 * size, 1.9 * size, 0.28 * size)));
    kit.add(box, fresh ? '#c4a684' : '#866a54', place(p.x, p.y + 0.3 * size, p.z + 0.1 * size, 0, new THREE.Vector3(0.5 * size, 0.06 * size, 0.08 * size)));
    for (const k of [-1, 1]) kit.add(sph, cut, place(p.x + k * 0.18 * size, p.y + 0.16 * size, p.z + 0.1 * size, 0, new THREE.Vector3(0.22 * size, 0.09 * size, 0.08 * size)));
    kit.add(box, cut, place(p.x, p.y - 0.28 * size, p.z + 0.11 * size, 0, new THREE.Vector3(0.3 * size, 0.04 * size, 0.05 * size)));
    kit.add(box, fresh ? '#a88a6c' : '#6e5644', place(p.x, p.y - 0.02 * size, p.z + 0.12 * size, 0, new THREE.Vector3(0.07 * size, 0.24 * size, 0.07 * size)));
    for (let i = 0; i < 2; i++) kit.add(box, cut, place(p.x, p.y - (0.6 + i * 0.12) * size, p.z + 0.06 * size, 0, new THREE.Vector3((0.5 - i * 0.14) * size, 0.03 * size, 0.04 * size)));
    sph.dispose();
    box.dispose();
  }

  /**
   * Olmo's maple: a broad trunk, its roots flaring over the ground, a wide crown of red; old (thick, furrowed, the face
   * weathered into the bark) or young (a slim tree, the face fresh-cut). `boughs`: its three great boughs as their own
   * groups (the test's branches), else in the dressing.
   */
  private maple(kit: Kit, p: THREE.Vector3, young: boolean, carved: boolean, boughs: boolean): void {
    const cyl = new THREE.CylinderGeometry(0.5, 0.62, 1, 10);
    const ico = new THREE.IcosahedronGeometry(0.5, 1);
    const g = young ? 0.55 : 1;
    kit.add(cyl, '#4a3226', place(p.x, p.y + 1.4 * g, p.z, 0.2, new THREE.Vector3(1.5 * g, 2.8 * g, 1.45 * g)));
    kit.add(cyl, '#563a2a', place(p.x + 0.05, p.y + 3.6 * g, p.z, 0.5, new THREE.Vector3(1.15 * g, 2.0 * g, 1.1 * g)));
    // Furrows down the trunk.
    if (!young)
      for (let i = 0; i < 9; i++) {
        const a = (i / 9) * Math.PI * 2;
        kit.add(cyl, '#2e2018', place(p.x + Math.cos(a) * 0.72, p.y + 1.4, p.z + Math.sin(a) * 0.7, a, new THREE.Vector3(0.09, 2.6, 0.09)));
      }
    // The roots flaring out over the ground.
    for (let i = 0; i < (young ? 5 : 9); i++) {
      const a = (i / (young ? 5 : 9)) * Math.PI * 2 + 0.3;
      kit.add(cyl, BARK[i % 3] as string, place(p.x + Math.cos(a) * 1.1 * g, p.y + 0.14, p.z + Math.sin(a) * 1.05 * g, a, new THREE.Vector3(0.42 * g, 1.7 * g, 0.42 * g), Math.PI / 2 - 0.28, 0));
    }
    // The crown: wide lumps of red (the boughs' own, apart, in the test).
    const crown = (k: Kit, cx: number, cy: number, cz: number, w: number, seed: number): void => {
      for (let i = 0; i < 7; i++) {
        const a = i * 1.7 + seed;
        const r = w * (0.4 + (i % 3) * 0.16);
        k.add(ico, RED[(i + seed) % RED.length] as string, place(cx + Math.cos(a) * r, cy + (i % 3) * 0.35 - 0.2, cz + Math.sin(a) * r * 0.7, a, w * (0.55 + (i % 2) * 0.2)));
      }
    };
    if (boughs) {
      // Three boughs out from the top of the trunk: west, up, east (each its own group, to fall).
      const dirs = [
        [-1, 0.42],
        [0.1, 0.9],
        [1, 0.4],
      ] as const;
      dirs.forEach(([dx, dy], i) => {
        const bk = new Kit();
        const len = 2.6;
        bk.add(cyl, '#4e3428', place(dx * len * 0.5, dy * len * 0.5, 0, 0, new THREE.Vector3(0.32, len, 0.32), 0, -Math.atan2(dx, dy)));
        crown(bk, dx * len, dy * len + 0.6, 0, 1.7, i * 3);
        const mesh = bk.build(this.vc(), `otono-bough-${i}`);
        const grp = new THREE.Group();
        grp.position.set(p.x, p.y + 4.4, p.z);
        if (mesh) {
          this.track(mesh.geometry);
          mesh.castShadow = true;
          grp.add(mesh);
        }
        grp.name = `otono-bough-${i}`;
        this.group.add(grp);
        this.boughs.push(grp);
      });
    } else crown(kit, p.x, p.y + 5.4 * g, p.z, 2.4 * g, 1);
    if (carved) this.face(kit, new THREE.Vector3(p.x, p.y + (young ? 1.05 : 1.55), p.z + (young ? 0.36 : 0.78)), young ? 0.36 : 0.46, young);
    this.litter(kit, p, 22, 7, RED);
    cyl.dispose();
    ico.dispose();
  }

  /** Houses of stone with dark roofs, a chimney each; a kiln mound by some (`lit`: windows and kilns alight). */
  private houses(kit: Kit, at: THREE.Vector3[], lit: boolean): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const roof = new THREE.ConeGeometry(0.75, 0.6, 4);
    const dome = new THREE.SphereGeometry(0.5, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2);
    at.forEach((p, i) => {
      const w = 1.1 + (i % 3) * 0.2;
      kit.add(box, STONE[i % 3] as string, place(p.x, p.y + 0.5, p.z, (i % 4) * 0.2, new THREE.Vector3(w, 1.0, w * 0.8)));
      kit.add(roof, ['#3a3432', '#4a3a34', '#2e2a2a'][i % 3] as string, place(p.x, p.y + 1.32, p.z, (i % 4) * 0.2 + Math.PI / 4, new THREE.Vector3(w * 1.08, 1.1, w * 0.9)));
      kit.add(box, '#4a403a', place(p.x + w * 0.25, p.y + 1.55, p.z, 0, new THREE.Vector3(0.14, 0.5, 0.14)));
      kit.add(box, '#2a1e18', place(p.x, p.y + 0.3, p.z + w * 0.41, 0, new THREE.Vector3(0.26, 0.5, 0.04)));
      if (i % 2 === 0) {
        kit.add(dome, '#36302c', place(p.x - w * 0.9, p.y, p.z + 0.4, 0, new THREE.Vector3(1.0, 0.8, 0.9)));
        if (lit) this.light(new THREE.Vector3(p.x - w * 0.9, p.y + 0.55, p.z + 0.4), '#ff8a3a', 1.6, 0.8, 1);
      }
      if (lit) this.light(new THREE.Vector3(p.x + w * 0.22, p.y + 0.55, p.z + w * 0.42), '#ffb070', 0.6, 0.9, 1);
    });
    box.dispose();
    roof.dispose();
    dome.dispose();
  }

  /** Cracks in the earth: dark jagged lines (merged). */
  private cracks(kit: Kit, p: THREE.Vector3, n: number, seed: number): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    for (let i = 0; i < n; i++) {
      let x = p.x + Math.cos(seed + i * 2.2) * 1.2;
      let z = p.z + Math.sin(seed + i * 2.2) * 1.2;
      let a = seed + i * 1.3;
      for (let j = 0; j < 5; j++) {
        a += Math.sin(seed * 3 + i * 7 + j * 5) * 0.8;
        const len = 0.5 + ((i + j) % 3) * 0.2;
        kit.add(box, '#140e0c', place(x + Math.cos(a) * len * 0.5, p.y + 0.004, z + Math.sin(a) * len * 0.5, -a, new THREE.Vector3(len, 0.008, 0.06 - j * 0.008)));
        x += Math.cos(a) * len;
        z += Math.sin(a) * len;
      }
    }
    box.dispose();
  }

  /** Trees thick past the board's rim (outside `R`), every so often; leaves between. */
  private rim(kit: Kit, R: number, every: number, young = false, skip?: (x: number, z: number) => boolean): void {
    for (let r = -R - 4; r <= R + 4; r++)
      for (let q = -R - 4; q <= R + 4; q++) {
        const d = Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r));
        if (d <= R || d > R + 3) continue;
        const k = Math.sin(q * 12.9 + r * 78.2) * 0.5 + 0.5;
        const p = this.hex(q, r);
        if (skip?.(p.x, p.z)) continue;
        if ((q * 7 + r * 13 + 99) % every === 0) this.tree(kit, p, k, (d === R + 1 ? 0.85 : 1.1) * (young ? 0.75 : 1));
        else this.litter(kit, p, 4, q * 3 + r);
      }
  }

  /* ---------- act 1: the wood ---------- */

  private buildWood(kit: Kit, s: BattleState, cine: boolean): void {
    const L = leafLayout(s.seed);
    this.Ll = L;
    for (const [i, h] of L.trees.entries()) {
      const k = Math.sin(h.q * 12.9 + h.r * 78.2) * 0.5 + 0.5;
      this.tree(kit, this.hex(h.q, h.r), k, 0.72 + (i % 4) * 0.08, i % 7 === 3);
    }
    // Litter over the whole board, thicker off the way.
    for (let r = -L.radius; r <= L.radius; r++)
      for (let q = -L.radius; q <= L.radius; q++) {
        if (Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) > L.radius || (q * 5 + r * 3 + 40) % 2) continue;
        this.litter(kit, this.hex(q, r), 3, q * 7 + r * 3);
      }
    const m = this.hex(L.maple.q, L.maple.r);
    this.mapleAt = m;
    this.maple(kit, m, false, true, false);
    this.rim(kit, L.radius, 2, false, (x, z) => z > (L.radius - 1) * S * 0.866 && Math.abs(x) < 4 && cine);
    if (cine) {
      // Down the slope, Los Hornos: its houses, the cracks in the earth, the trees above them black at their tips.
      const at: THREE.Vector3[] = [];
      for (let i = 0; i < 16; i++) at.push(this.ctx.at(-8000 + (i % 8) * 2300 + (i >> 3) * 1100, (L.radius + 5 + (i >> 3) * 2) * 866, -0.8));
      this.houses(kit, at, true);
      for (let i = 0; i < 5; i++) this.cracks(kit, this.ctx.at(-6000 + i * 3000, (L.radius + 4) * 866, -0.7), 2, i * 3);
    }
    const lg = this.merged((k) => {
      const ico = new THREE.IcosahedronGeometry(0.5, 0);
      k.add(ico, '#ffffff', place(0, 0, 0, 0, new THREE.Vector3(0.34, 0.07, 0.24)));
      ico.dispose();
    });
    this.bits = this.inst(lg, this.vc(), 360, 'otono-furrows');
  }

  /** The furrows: a hump of leaves over each stalker under them, and the trail behind it. */
  private drawWood(s: BattleState): void {
    const bits = this.bits;
    const w = s.senda?.autumn;
    if (!bits || !w) return;
    let n = 0;
    const leaf = (x: number, y: number, z: number, k: number, lift: number): void => {
      if (n >= bits.instanceMatrix.count) return;
      this.q4.setFromEuler(this.e3.set(Math.sin(k * 3.1) * 0.5, k * 2.3, Math.cos(k * 1.7) * 0.5));
      this.m4.compose(this.v3.set(x, y + lift, z), this.q4, this.s3.set(1, 1, 1));
      bits.setMatrixAt(n, this.m4);
      bits.setColorAt(n++, this.c3.set((k * 7) % 2 < 1 ? (RED[Math.floor(k * 11) % RED.length] as string) : (OCHRE[Math.floor(k * 13) % OCHRE.length] as string)));
    };
    const seen = new Set<number>();
    for (const id of Object.keys(w.sk)) {
      const e = s.enemies.find((x) => x.id === Number(id));
      const m = w.sk[Number(id)] as number[];
      if (!e || e.hp <= 0) continue;
      seen.add(e.id);
      const hidden = m[0] === 0 || m[0] === 1 || m[0] === 3;
      const p = this.ctx.at(e.x, e.y, 0);
      let tr = this.trail.get(e.id);
      if (!tr) {
        tr = [];
        this.trail.set(e.id, tr);
      }
      if (hidden && (!tr.length || (tr[tr.length - 1] as THREE.Vector3).distanceTo(p) > 0.3)) {
        tr.push(p.clone());
        if (tr.length > 10) tr.shift();
      }
      if (!hidden) continue;
      // The hump: higher while it heaves.
      const heave = m[0] === 1 ? 0.25 + 0.12 * Math.sin(s.tick * 2.1) : 0;
      for (let i = 0; i < 9; i++) {
        const a = i * 0.7 + e.id;
        const r = 0.12 + (i % 3) * 0.12;
        leaf(p.x + Math.cos(a) * r, p.y, p.z + Math.sin(a) * r, e.id + i, 0.12 + heave + (2 - (i % 3)) * 0.06);
      }
      // The furrow behind: leaves pushed aside, lower each step back.
      tr.forEach((q, j) => {
        const f = j / tr.length;
        for (let i = 0; i < 3; i++) leaf(q.x + Math.cos(i * 2.1 + j) * 0.22, q.y, q.z + Math.sin(i * 2.1 + j) * 0.22, e.id * 3 + j + i, 0.04 + f * 0.06);
      });
    }
    for (const id of [...this.trail.keys()]) if (!seen.has(id)) this.trail.delete(id);
    bits.count = n;
    bits.instanceMatrix.needsUpdate = true;
    if (bits.instanceColor) bits.instanceColor.needsUpdate = true;
    // The silk: a weaver spinning, a pale band to where Ceda was.
    let k = 0;
    for (const id of Object.keys(w.wv)) {
      const m = w.wv[Number(id)] as number[];
      const e = s.enemies.find((x) => x.id === Number(id));
      const line = this.silk[k];
      if (!line || !e || e.hp <= 0 || (m[1] as number) < 0) continue;
      const a = this.ctx.at(e.x, e.y, 0.08);
      const b = this.ctx.at(m[3] as number, m[4] as number, 0.08);
      const len = a.distanceTo(b);
      line.position.copy(a);
      line.rotation.set(0, Math.atan2(b.x - a.x, b.z - a.z), 0);
      line.scale.set(0.12 + 0.06 * Math.sin(s.tick), 1, len);
      line.visible = true;
      k++;
    }
    for (let i = k; i < this.silk.length; i++) (this.silk[i] as THREE.Mesh).visible = false;
  }

  /* ---------- act 2: the memory ---------- */

  private buildPast(kit: Kit, s: BattleState, carve: boolean): void {
    const L = pastLayout(s.seed);
    this.Pl = L;
    for (const [i, h] of L.trees.entries()) {
      const k = Math.sin(h.q * 12.9 + h.r * 78.2) * 0.5 + 0.5;
      this.tree(kit, this.hex(h.q, h.r), k, 0.7 + (i % 3) * 0.08);
    }
    for (let r = -L.radius; r <= L.radius; r++)
      for (let q = -L.radius; q <= L.radius; q++) {
        if (Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) > L.radius || (q * 5 + r * 3 + 41) % 3) continue;
        this.litter(kit, this.hex(q, r), 3, q * 7 + r * 3, OCHRE);
      }
    const m = this.hex(L.maple.q, L.maple.r);
    this.mapleAt = m;
    this.maple(kit, m, true, carve, false);
    // The six houses of Los Hornos at the foot, the colliers' kilns.
    const at = L.houses.filter((_, i) => i % 3 === 0).slice(0, 6).map((h) => this.hex(h.q, h.r, -0.05));
    this.houses(kit, at, carve);
    this.rim(kit, L.radius, 3, true, (_x, z) => z > (L.houseRow - 1) * S * 0.866);
    // The roots: a piece from each cell's one above (the top's from the maple's foot), raised over the ground.
    this.nodeAt = L.nodes.map((n) => this.hex(n.q, n.r, 0.05));
    const cg = this.track(new THREE.CylinderGeometry(1, 1, 1, 7).translate(0, 0.5, 0));
    const rm = this.track(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.5, metalness: 0.05, flatShading: true }));
    this.roots = this.inst(cg, rm, L.nodes.length, 'otono-roots');
    this.roots.castShadow = true;
    const bg = this.track(new THREE.SphereGeometry(0.5, 10, 8));
    const bm = this.track(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.25, metalness: 0.1 }));
    this.blobs = this.inst(bg, bm, 40, 'otono-heads');
    const mg = this.track(new THREE.ConeGeometry(0.12, 1, 5).translate(0, 0.5, 0));
    this.marks = this.inst(mg, rm, 60, 'otono-cuts');
    this.drawPast(null);
  }

  private piece(m: THREE.InstancedMesh, i: number, a: THREE.Vector3, b: THREE.Vector3, r: number, c: THREE.Color): void {
    this.v4.subVectors(b, a);
    const d = this.v4.length();
    this.q4.setFromUnitVectors(this.up, this.v4.normalize());
    this.m4.compose(a, this.q4, this.s3.set(r, Math.max(0.01, d), r));
    m.setMatrixAt(i, this.m4);
    m.setColorAt(i, c);
  }

  private drawPast(s: BattleState | null): void {
    const L = this.Pl;
    const roots = this.roots;
    const blobs = this.blobs;
    const marks = this.marks;
    if (!L || !roots || !blobs || !marks) return;
    const w = s?.senda?.autumn;
    const rot = w?.rot ?? [];
    // (Written when a cell changes only: docs/RENDIMIENTO.md, «Un buffer instanciado…».)
    const key = rot.join('');
    if (key !== this.rootKey || !roots.count) {
      this.rootKey = key;
      L.nodes.forEach((n, i) => {
        const b = this.nodeAt[i] as THREE.Vector3;
        const a = n.up >= 0 ? (this.nodeAt[n.up] as THREE.Vector3) : (this.mapleAt as THREE.Vector3).clone().setY(b.y);
        const v = rot[i] ?? 0;
        const c = v === 1 ? ROT : v === 2 ? CUT : v === 3 ? SPENT : SOUND;
        this.piece(roots, i, a, b, n.up >= 0 ? 0.13 : 0.18, c);
      });
      roots.count = L.nodes.length;
      roots.instanceMatrix.needsUpdate = true;
      if (roots.instanceColor) roots.instanceColor.needsUpdate = true;
      // The cuts: the druid's root driven in, green, by a pale fresh end.
      let k = 0;
      rot.forEach((v, i) => {
        if (v !== 2 || k >= marks.instanceMatrix.count) return;
        const p = this.nodeAt[i] as THREE.Vector3;
        this.m4.compose(this.v3.copy(p), this.q4.identity(), this.s3.set(1.2, 1.1, 1.2));
        marks.setMatrixAt(k, this.m4);
        marks.setColorAt(k++, DRUID);
      });
      marks.count = k;
      marks.instanceMatrix.needsUpdate = true;
      if (marks.instanceColor) marks.instanceColor.needsUpdate = true;
    }
    // The heads: black and swelling, a pale rim of light round them (read in the memory's grey).
    let h = 0;
    if (w)
      for (const hd of w.heads) {
        if (h >= blobs.instanceMatrix.count) break;
        const p = this.nodeAt[hd.i] as THREE.Vector3;
        const f = 0.55 + 0.12 * Math.sin(this.now * 7 + hd.i);
        this.m4.compose(this.v3.copy(p).setY(p.y + 0.22), this.q4.identity(), this.s3.set(f * 1.3, f, f * 1.3));
        blobs.setMatrixAt(h, this.m4);
        blobs.setColorAt(h++, ROT);
      }
    blobs.count = h;
    blobs.instanceMatrix.needsUpdate = true;
    if (blobs.instanceColor) blobs.instanceColor.needsUpdate = true;
  }

  /* ---------- act 3: what lies below ---------- */

  /**
   * Inside the Grandsire: the wood round the net, a cell of it: a hex slab wide enough to overlap its neighbours (the
   * mass reads as one wall of dark wood, never a ring of columns), its top's grain, an amber crack or a vein now and then.
   */
  private pillar(kit: Kit, p: THREE.Vector3, k: number): void {
    const hex = new THREE.CylinderGeometry(0.88, 0.95, 1, 6);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const h = 1.7;
    kit.add(hex, ['#2a1a0e', '#2e1c10', '#26170c'][Math.floor(k * 2.99)] as string, place(p.x, p.y + h / 2, p.z, 0, new THREE.Vector3(1, h, 1)));
    kit.add(box, '#3a2414', place(p.x, p.y + h + 0.005, p.z, 0.35 + (k - 0.5) * 0.3, new THREE.Vector3(1.0, 0.01, 0.05)));
    if (k > 0.82) {
      const a = k * 11;
      kit.add(box, '#ffb050', place(p.x, p.y + h + 0.012, p.z, a, new THREE.Vector3(0.45, 0.012, 0.045)));
      this.light(new THREE.Vector3(p.x, p.y + h + 0.1, p.z), '#ffa040', 1.1, 0.3, 0.5);
    }
    hex.dispose();
    box.dispose();
  }

  /** Inside the Grandsire: chips and fibres of wood over the dark floor (merged). */
  private chips(kit: Kit, p: THREE.Vector3, seed: number): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    for (let i = 0; i < 3; i++) {
      const a = seed * 2.3 + i * 2.39;
      const d = 0.3 + ((i * 37 + seed * 11) % 10) * 0.07;
      kit.add(box, (['#6a4a2e', '#3a2416', '#7a5636'][(i + seed) % 3] as string), place(p.x + Math.cos(a) * d, p.y + 0.02, p.z + Math.sin(a) * d, a, new THREE.Vector3(0.32, 0.04, 0.06)));
    }
    box.dispose();
  }

  /** Inside the Grandsire: the walls round the net, columns and lumps of wood close together. */
  private pillarRim(kit: Kit, R: number): void {
    for (let r = -R - 3; r <= R + 3; r++)
      for (let q = -R - 3; q <= R + 3; q++) {
        const d = Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r));
        if (d <= R || d > R + 2) continue;
        this.pillar(kit, this.hex(q, r), Math.sin(q * 12.9 + r * 78.2) * 0.5 + 0.5);
      }
  }

  /** The Carcoma's chamber's mouth where the maple stood: an arch of black roots over a dark hole, a pale glow breathing in it. */
  private chamberMouth(kit: Kit, p: THREE.Vector3): void {
    const cyl = new THREE.CylinderGeometry(1, 1, 1, 7);
    kit.add(new THREE.CylinderGeometry(1.5, 1.7, 0.2, 14), '#0c080a', place(p.x, p.y + 0.02, p.z));
    for (let i = 0; i < 9; i++) {
      const a = Math.PI * (i / 8);
      const a0 = new THREE.Vector3(p.x + Math.cos(a) * 1.9, p.y, p.z + Math.sin(a) * 0.4 - 0.6);
      const a1 = new THREE.Vector3(p.x + Math.cos(a) * 0.9, p.y + 2.6 * Math.sin(a) + 0.4, p.z - 0.4);
      const d = a1.clone().sub(a0);
      const L = d.length();
      const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.normalize());
      kit.add(cyl, i % 2 ? '#141016' : '#221a24', new THREE.Matrix4().compose(a0.clone().add(a1).multiplyScalar(0.5), q, new THREE.Vector3(0.22, L, 0.2)));
    }
    this.light(p.clone().setY(p.y + 0.6), '#b8a8c8', 3.4, 0.45, 1);
    cyl.dispose();
  }

  private buildRoots(kit: Kit, s: BattleState, close: boolean): void {
    const L = underLayout(s.seed, this.deep ? DRUID_PATH.net.pre : DRUID_PATH.under.pre);
    this.Ul = L;
    for (const [i, h] of L.trees.entries()) {
      const k = Math.sin(h.q * 12.9 + h.r * 78.2) * 0.5 + 0.5;
      if (this.deep) this.pillar(kit, this.hex(h.q, h.r), k);
      else this.tree(kit, this.hex(h.q, h.r), k, 0.9 + (i % 4) * 0.1, i % 5 === 2);
    }
    const rootAt = new Set(L.cells.map((c) => `${c.q},${c.r}`));
    for (let r = -L.radius; r <= L.radius; r++)
      for (let q = -L.radius; q <= L.radius; q++) {
        if (Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) > L.radius || rootAt.has(`${q},${r}`) || (q * 5 + r * 3 + 40) % 2) continue;
        if (this.deep) this.chips(kit, this.hex(q, r), q * 7 + r * 3);
        else this.litter(kit, this.hex(q, r), 3, q * 7 + r * 3);
      }
    const m = this.hex(L.maple.q, L.maple.r);
    this.mapleAt = m;
    if (this.deep) {
      this.chamberMouth(kit, m);
      this.pillarRim(kit, L.radius);
    } else {
      this.maple(kit, m, false, true, true);
      this.rim(kit, L.radius, 2, false, close ? (_x, z) => z > (L.radius - 1) * S * 0.866 : undefined);
    }
    if (close) {
      // Below, the lights of Los Hornos.
      const at: THREE.Vector3[] = [];
      for (let i = 0; i < 12; i++) at.push(this.ctx.at(-7000 + (i % 6) * 2800, (L.radius + 6 + (i >> 1) % 2 * 2) * 866, -1.2));
      this.houses(kit, at, true);
    }
    // The knot: a black burl, swollen, where the rot gets in.
    const kp = this.hex(L.knot.q, L.knot.r);
    this.knotAt = kp;
    const ico = new THREE.IcosahedronGeometry(0.5, 1);
    for (let i = 0; i < 7; i++) {
      const a = i * 0.9;
      kit.add(ico, i % 2 ? '#1a141c' : '#241a24', place(kp.x + Math.cos(a) * 0.35, kp.y + 0.2 + (i % 3) * 0.12, kp.z + Math.sin(a) * 0.3, a, new THREE.Vector3(0.62, 0.5, 0.55)));
    }
    ico.dispose();
    this.light(kp.clone().setY(kp.y + 0.6), '#9a7aa8', 2.6, 0.55, 1);
    this.cellAt = L.cells.map((c) => this.hex(c.q, c.r));
    // The roots in sight: rotten black and glossy, sound brown with Olmo's notch on each piece.
    const cg = this.track(new THREE.CylinderGeometry(1, 1, 1, 7).translate(0, 0.5, 0));
    const vm = this.track(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.42, metalness: 0.08, flatShading: true }));
    const pieces: { a: THREE.Vector3; b: THREE.Vector3; cell: number; strand: number }[] = [];
    L.strands.forEach((st, k) => {
      const at = L.at[k] as number[];
      const from = st.rot ? 1 : 0;
      const to = st.rot ? st.vis : st.vis - 1;
      for (let j = from; j <= to; j++) {
        const i = at[j] as number;
        if (i < 0) continue;
        const b = (this.cellAt[i] as THREE.Vector3).clone().setY((this.cellAt[i] as THREE.Vector3).y + 0.04);
        const pj = j - 1 >= 0 ? at[j - 1] : -1;
        const a = st.rot && j === 1 ? kp.clone() : !st.rot && j === 0 ? m.clone() : pj !== undefined && pj >= 0 ? (this.cellAt[pj] as THREE.Vector3).clone() : b.clone();
        a.setY(b.y);
        pieces.push({ a, b, cell: i, strand: k });
        // Olmo's notch on a sound one: a pale wedge cut in its top.
        if (!st.rot) {
          const mid = a.clone().lerp(b, 0.5);
          const notch = new THREE.BoxGeometry(1, 1, 1);
          kit.add(notch, '#e4d8c4', place(mid.x, mid.y + 0.22, mid.z, Math.atan2(b.x - a.x, b.z - a.z), new THREE.Vector3(0.24, 0.06, 0.1)));
          kit.add(notch, '#2a1a12', place(mid.x, mid.y + 0.24, mid.z, Math.atan2(b.x - a.x, b.z - a.z) + 0.6, new THREE.Vector3(0.18, 0.05, 0.04)));
          notch.dispose();
        }
      }
    });
    this.visRoots = this.inst(cg, vm, Math.max(1, pieces.length), 'otono-vis-roots');
    this.visRoots.castShadow = true;
    pieces.forEach((pc, i) => {
      const st = L.strands[pc.strand];
      this.piece(this.visRoots as THREE.InstancedMesh, i, pc.a, pc.b, st?.rot ? 0.2 : 0.17, st?.rot ? ROT : SOUND);
      this.visOf.push({ cell: pc.cell, strand: pc.strand });
    });
    this.visRoots.count = pieces.length;
    // The mushrooms: on each cell, one per root under it; big, so a phone counts them.
    const stemG = this.merged((k) => {
      const cyl = new THREE.CylinderGeometry(0.07, 0.1, 1, 7).translate(0, 0.5, 0);
      k.add(cyl, '#e8e4dc', place(0, 0, 0, 0, 1));
      cyl.dispose();
    });
    const capG = this.merged((k) => {
      const sph = new THREE.SphereGeometry(0.5, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55);
      k.add(sph, '#ffffff', place(0, 0, 0, 0, 1));
      // Its gills' rim, a shade darker.
      const tor = new THREE.TorusGeometry(0.46, 0.04, 4, 14).rotateX(Math.PI / 2);
      k.add(tor, '#c8c8cc', place(0, 0.04, 0, 0, 1));
      sph.dispose();
      tor.dispose();
    });
    let total = 0;
    for (const n of L.mush) total += n;
    this.stems = this.inst(stemG, this.vc(), Math.max(1, total), 'otono-stems');
    const capMat = this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.55, metalness: 0.02, flatShading: false, emissive: '#000000' }));
    this.caps = this.inst(capG, capMat, Math.max(1, total), 'otono-caps', 1);
    this.stems.castShadow = true;
    this.caps.castShadow = true;
    let q = 0;
    L.cells.forEach((_c, i) => {
      const n = L.mush[i] as number;
      if (!n) return;
      const p = this.cellAt[i] as THREE.Vector3;
      // Which of the roots under it are rotten (a black cap each), the rest sound (white).
      const under = (L.on[i] as number[]).filter((sIdx) => {
        const st = L.strands[sIdx];
        const j = (L.at[sIdx] as number[]).indexOf(i);
        return st && !(st.rot ? j >= 1 && j <= st.vis : j < st.vis);
      });
      for (let k = 0; k < n; k++) {
        const a = n === 1 ? 0 : (k / n) * Math.PI * 2 + 0.5;
        const d = n === 1 ? 0 : 0.27;
        const x = p.x + Math.cos(a) * d;
        const z = p.z + Math.sin(a) * d;
        const tall = 0.62 + ((i * 7 + k * 3) % 4) * 0.05;
        this.m4.compose(this.v3.set(x, p.y, z), this.q4.setFromEuler(this.e3.set(0, a, Math.sin(i + k) * 0.08)), this.s3.set(1, tall, 1));
        (this.stems as THREE.InstancedMesh).setMatrixAt(q, this.m4);
        (this.stems as THREE.InstancedMesh).setColorAt(q, WHITE);
        this.m4.compose(this.v3.set(x, p.y + tall, z), this.q4.setFromEuler(this.e3.set(0, a, Math.sin(i + k) * 0.08)), this.s3.set(0.5, 0.36, 0.5));
        (this.caps as THREE.InstancedMesh).setMatrixAt(q, this.m4);
        const black = !!L.strands[under[k] ?? -1]?.rot;
        this.capOf.push(i);
        this.capBlack.push(black);
        (this.caps as THREE.InstancedMesh).setColorAt(q, black ? BLACK : WHITE);
        q++;
      }
    });
    // (The close: the mushrooms' moment was the fight's; Hob, the face and Elvira have the clearing.)
    this.stems.count = close ? 0 : q;
    this.caps.count = close ? 0 : q;
    // The rot's stain on the ground, along its roots as it comes.
    const dg = this.track(new THREE.CircleGeometry(0.62, 12).rotateX(-Math.PI / 2));
    const dm = this.track(new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.82, depthWrite: false }));
    this.stain = this.inst(dg, dm, 120, 'otono-stain', 2);
    const mg = this.track(new THREE.ConeGeometry(0.12, 1, 5).translate(0, 0.5, 0));
    const mm = this.track(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.6 }));
    this.marks = this.inst(mg, mm, 30, 'otono-cuts');
    if (close) {
      this.outT = 0;
      for (let i = 0; i < this.capBlack.length; i++) this.capBlack[i] = false;
    }
    this.drawRoots(null);
  }

  private drawRoots(s: BattleState | null): void {
    const L = this.Ul;
    if (!L || !this.caps || !this.stain || !this.marks || !this.visRoots) return;
    const w = s?.senda?.autumn;
    const r = s?.rules?.senda?.autumn?.under;
    // The caps: black over the rot, white over sound; white one by one from the maple once the rot is out.
    const outK = this.outT >= 0 ? Math.floor(this.outT * 18) : -1;
    const capKey = `${outK}`;
    if (capKey !== this.capKey) {
      this.capKey = capKey;
      const order = this.capOf.map((c, i) => ({ i, d: (this.cellAt[c] as THREE.Vector3).distanceTo(this.mapleAt as THREE.Vector3) })).sort((a, b) => a.d - b.d);
      const rank = new Array<number>(order.length);
      order.forEach((o, k) => (rank[o.i] = k));
      this.capBlack.forEach((b, i) => this.caps?.setColorAt(i, b && !(outK >= 0 && (rank[i] as number) < outK) ? BLACK : WHITE));
      if (this.caps.instanceColor) this.caps.instanceColor.needsUpdate = true;
    }
    if (!w || !r || !w.front.length) return;
    // The stain: every cell the rot has along its roots; the cuts.
    const key = `${w.front.join(',')}|${w.cut.join('')}|${w.done}`;
    if (key === this.stainKey) return;
    this.stainKey = key;
    let n = 0;
    r.strands.forEach((st, k) => {
      if (!st.rot) return;
      const f = w.front[k] as number;
      for (let j = 1; j <= f && j < st.at.length; j++) {
        const i = st.at[j] as number;
        if (i < 0 || n >= (this.stain as THREE.InstancedMesh).instanceMatrix.count) continue;
        const p = this.cellAt[i] as THREE.Vector3;
        this.m4.compose(this.v3.set(p.x, p.y + 0.03, p.z), this.q4.identity(), this.s3.set(j === f ? 1.25 : 1, 1, j === f ? 1.25 : 1));
        (this.stain as THREE.InstancedMesh).setMatrixAt(n, this.m4);
        (this.stain as THREE.InstancedMesh).setColorAt(n++, this.c3.copy(ROT).lerp(ROT_GLOW, j === f && w.done < 0 ? 0.14 : 0));
      }
    });
    this.stain.count = n;
    this.stain.instanceMatrix.needsUpdate = true;
    if (this.stain.instanceColor) this.stain.instanceColor.needsUpdate = true;
    let c = 0;
    w.cut.forEach((v, i) => {
      if (!v || c >= (this.marks as THREE.InstancedMesh).instanceMatrix.count) return;
      const p = this.cellAt[i] as THREE.Vector3;
      this.m4.compose(this.v3.set(p.x + 0.42, p.y, p.z + 0.3), this.q4.identity(), this.s3.set(1.4, 1.3, 1.4));
      (this.marks as THREE.InstancedMesh).setMatrixAt(c, this.m4);
      (this.marks as THREE.InstancedMesh).setColorAt(c++, DRUID);
    });
    this.marks.count = c;
    this.marks.instanceMatrix.needsUpdate = true;
    if (this.marks.instanceColor) this.marks.instanceColor.needsUpdate = true;
    // The roots in sight that the rot has come along go dull once it is out.
    const vk = `${w.done}`;
    if (vk !== this.visKey && w.done >= 0) {
      this.visKey = vk;
      this.visOf.forEach((v, i) => {
        if (L.strands[v.strand]?.rot) this.visRoots?.setColorAt(i, SPENT);
      });
      if (this.visRoots.instanceColor) this.visRoots.instanceColor.needsUpdate = true;
    }
  }

  /* ---------- the town ---------- */

  private buildTown(kit: Kit, night: boolean): void {
    // A street of Los Hornos: houses of stone up both sides, kilns smoking, cracks in the earth, the slope above.
    const at: THREE.Vector3[] = [];
    for (let i = 0; i < 6; i++) at.push(this.ctx.at(-5200 + i * 2100, i % 2 ? -3300 : -3900, 0));
    for (let i = 0; i < 5; i++) at.push(this.ctx.at(-4300 + i * 2200, 4200 + (i % 2) * 500, 0));
    this.houses(kit, at, night);
    // Ruda's house: the one behind her, its kiln and its door.
    const door = this.ctx.at(0, -1800, 0);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const roof = new THREE.ConeGeometry(0.75, 0.6, 4);
    kit.add(box, STONE[0] as string, place(door.x, door.y + 0.75, door.z - 0.6, 0, new THREE.Vector3(3.0, 1.5, 1.8)));
    kit.add(roof, '#3a3432', place(door.x, door.y + 1.9, door.z - 0.6, Math.PI / 4, new THREE.Vector3(3.0, 1.4, 2.2)));
    kit.add(box, '#2a1e18', place(door.x, door.y + 0.55, door.z + 0.31, 0, new THREE.Vector3(0.6, 1.1, 0.06)));
    kit.add(box, '#4a403a', place(door.x + 1.0, door.y + 2.3, door.z - 0.6, 0, new THREE.Vector3(0.22, 0.9, 0.22)));
    if (night) this.light(door.clone().setY(door.y + 0.6).setZ(door.z + 0.4), '#ffb070', 1.2, 0.9, 1);
    box.dispose();
    roof.dispose();
    for (let i = 0; i < 6; i++) this.cracks(kit, this.ctx.at(-3600 + i * 1500, 600 + (i % 2) * 900, 0), 2, i * 5 + 1);
    // Leaves blown into the street; the trees up the slope, their tips black.
    for (let i = 0; i < 30; i++) this.litter(kit, this.ctx.at(-6000 + (i % 10) * 1300, -800 + (i % 3) * 1000, 0), 3, i * 3);
    for (let i = 0; i < 9; i++) {
      const p = this.ctx.at(-8000 + i * 2000, -6400 - (i % 2) * 900, 0);
      this.tree(kit, p, (i * 0.37) % 1, 1.1, i % 3 === 0);
      const ico = new THREE.IcosahedronGeometry(0.5, 0);
      kit.add(ico, '#141012', place(p.x, p.y + 4.3 + (i % 2) * 0.4, p.z, i, 0.9));
      ico.dispose();
    }
  }

  /* ---------- painting ---------- */

  apply(terrain: TerrainBuild, hour: Hour = 'day'): void {
    this.terrain = terrain;
    const c = new THREE.Color();
    const col = terrain.mesh.geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pos = terrain.mesh.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    smoothTiles(terrain.mesh.geometry, terrain.heights, this.map.radius, terrain.ground);
    const way = this.Ll?.way ?? [];
    const town = this.place === 'hornosTown' || this.place === 'rudaYard';
    if (col && pos) {
      terrain.mesh.updateMatrixWorld(true);
      const v = new THREE.Vector3();
      for (let i = 0; i < col.count; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(terrain.mesh.matrixWorld);
        const lum = col.getX(i) * 0.3 + col.getY(i) * 0.59 + col.getZ(i) * 0.11;
        const k = 0.5 + 0.5 * Math.sin(v.x * 1.3 + v.z * 0.7) * Math.cos(v.z * 1.1 - v.x * 0.4);
        // Ceda's way: trodden earth through the litter.
        let onWay = false;
        for (let j = 1; j < way.length && !onWay; j++) {
          const a = way[j - 1] as { x: number; y: number };
          const b = way[j] as { x: number; y: number };
          const ax = a.x * K;
          const az = a.y * K;
          const bx = b.x * K;
          const bz = b.y * K;
          const dx = bx - ax;
          const dz = bz - az;
          const t = Math.max(0, Math.min(1, ((v.x - ax) * dx + (v.z - az) * dz) / Math.max(1e-6, dx * dx + dz * dz)));
          if (Math.hypot(v.x - ax - dx * t, v.z - az - dz * t) < 0.62) onWay = true;
        }
        if (this.deep) c.set(HEARTWOOD[Math.floor(k * 3.99)] as string).multiplyScalar(0.85 + lum * 0.25);
        else if (town) c.set(['#4e463e', '#58504a', '#463e38'][Math.floor(k * 2.99)] as string).multiplyScalar(0.75 + lum * 0.35);
        else if (onWay) c.set(EARTH[Math.floor(k * 2.99)] as string).multiplyScalar(0.85 + lum * 0.3);
        else c.set(LITTER[Math.floor(k * 4.99)] as string).multiplyScalar(0.9 + lum * 0.15);
        col.setXYZ(i, c.r, c.g, c.b);
      }
      col.needsUpdate = true;
    }
    terrain.decor.traverse((o) => {
      if (o.name === 'roads') o.visible = false;
      if (o.name === 'outskirts-ground') {
        const g = (o as THREE.Mesh).geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
        if (g) {
          for (let i = 0; i < g.count; i++) {
            const lum = g.getX(i) * 0.3 + g.getY(i) * 0.59 + g.getZ(i) * 0.11;
            c.set(this.deep ? '#2a1a10' : town ? '#3e3832' : '#6e3420').multiplyScalar(0.7 + lum * 0.4);
            g.setXYZ(i, c.r, c.g, c.b);
          }
          g.needsUpdate = true;
        }
      }
      // The board's own trees and rocks give way to the wood's.
      if ((o as THREE.InstancedMesh).isInstancedMesh) o.visible = false;
    });
    if (terrain.fog) terrain.fog.group.visible = true;
    this.dome = new SkyDome();
    this.group.add(this.dome.mesh);
    this.paint(hour);
  }

  paint(hour: Hour, weather?: string): void {
    this.hour = hour;
    const look = this.deep ? DEEP_LOOK : (HOURS[hour] ?? HOURS.day);
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog[0]);
      scene.fog.near = weather === 'fog' ? look.fog[1] * 0.75 : look.fog[1];
      scene.fog.far = weather === 'fog' ? look.fog[2] * 0.7 : look.fog[2];
    }
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    this.dome?.set(look.sky[0], look.sky[1], look.sky[2]);
    if (this.dome) this.dome.u.uSunDir.value.set(0.6, hour === 'dusk' ? 0.08 : 0.3, 0.3);
    this.terrain?.fog?.tint({ low: look.fog[0], mid: look.fog[0], far: look.bg, alpha: 1 });
  }

  /* ---------- each tick ---------- */

  tick(s: BattleState): void {
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const p = this.ctx.at(e.x, e.y, 0.2);
      if (e.kind === 'heave') {
        this.ctx.vfx.burst(p, { n: 12, color: '#c4442a', speed: 1.2, up: 1.6, size: 0.12, life: 14, gravity: 1 });
      } else if (e.kind === 'surface') {
        this.ctx.shake(0.15);
        this.ctx.vfx.burst(p, { n: 26, color: '#b4381e', speed: 2.2, up: 2.6, size: 0.14, life: 18, gravity: 1 });
        this.ctx.vfx.burst(p, { n: 14, color: '#c27030', speed: 1.8, up: 2.2, size: 0.12, life: 16, gravity: 1 });
      } else if (e.kind === 'caught') {
        this.ctx.flare(p.clone().setY(p.y + 0.6), '#a8e078', 2.2, 14);
        this.ctx.vfx.burst(p, { n: 20, color: '#8ed060', speed: 1.4, up: 2.4, size: 0.12, life: 16 });
        this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.06), 0.3, 1.6, '#8ed060', 10, 0.9);
      } else if (e.kind === 'cocoon') {
        this.ctx.vfx.burst(p.setY(p.y + 0.8), { n: 18, color: '#eef2f6', speed: 1.2, up: 1.0, size: 0.1, life: 18 });
      } else if (e.kind === 'free') {
        this.ctx.vfx.burst(p.setY(p.y + 0.8), { n: 22, color: '#eef2f6', speed: 2.2, up: 1.6, size: 0.1, life: 14 });
      } else if (e.kind === 'rootCut') {
        const bad = e.npc === 'bad' || e.npc === 'vain';
        this.ctx.flare(p.clone().setY(p.y + 0.5), bad ? '#ff4f3a' : '#c8f0a0', 2.6, 14);
        this.ctx.vfx.burst(p, { n: 22, color: bad ? '#ff6a4a' : '#8ed060', speed: 1.8, up: 2.6, size: 0.13, life: 16 });
        this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.06), 0.3, 1.3, bad ? '#ff4f3a' : '#d8f0c0', 10, 0.9);
        if (bad) this.ctx.shake(0.3);
      } else if (e.kind === 'headDie') {
        this.ctx.flare(p.clone().setY(p.y + 0.5), '#e8eef4', 3, 18);
        this.ctx.vfx.burst(p, { n: 26, color: '#1a1418', speed: 1.4, up: 2.0, size: 0.24, life: 22, smoke: true });
      } else if (e.kind === 'rotIn') {
        this.ctx.vfx.burst(p, { n: 20, color: '#141016', speed: 1.2, up: 1.6, size: 0.24, life: 20, smoke: true });
      } else if (e.kind === 'rotStep') {
        if (this.Ul) this.ctx.vfx.burst(p, { n: 6, color: '#2a1e2c', speed: 0.6, up: 1.0, size: 0.2, life: 14, smoke: true });
      } else if (e.kind === 'houses') {
        this.ctx.shake(0.6);
        this.ctx.flare(p.clone().setY(p.y + 1), '#ff4f3a', 4, 20);
      } else if (e.kind === 'seed') {
        this.seedFlash = 1;
        this.ctx.flare(p.clone().setY(p.y + 2), '#fff4e8', 5, 26);
      } else if (e.kind === 'chop') {
        this.ctx.vfx.burst(p.setY(p.y + 0.6), { n: 5, color: '#c8ccd2', speed: 1.4, up: 0.8, size: 0.06, life: 8 });
      } else if (e.kind === 'branch') {
        const i = Math.min(2, Math.max(0, (e.n ?? 1) - 1));
        this.fall[i] = 0.001;
        const b = this.boughs[i];
        if (b) {
          this.ctx.shake(0.7);
          this.ctx.vfx.burst(b.position.clone().setY(b.position.y + 1.5), { n: 40, color: '#c0402a', speed: 2.6, up: 1.2, size: 0.16, life: 28, gravity: 1 });
        }
        if (e.npc === 'web' || e.npc === 'rot') this.ctx.vfx.burst(p, { n: 16, color: '#1a1418', speed: 1.6, up: 2, size: 0.2, life: 18, smoke: true });
      } else if (e.kind === 'inMaple') {
        this.ctx.flare(p.clone().setY(p.y + 2.2), '#9a7aa8', 4, 22);
      } else if (e.kind === 'rotOut') {
        this.outT = 0;
        this.seedFlash = 1;
        if (this.mapleAt) this.ctx.flare(this.mapleAt.clone().setY(this.mapleAt.y + 5), '#fff4e8', 6, 30);
      } else if (e.kind === 'leap') {
        this.ctx.shake(0.6);
        this.ctx.vfx.burst(p, { n: 28, color: '#5a4a44', speed: 2.8, up: 2.2, size: 0.18, life: 18, gravity: 1 });
        this.ctx.vfx.burst(p, { n: 20, color: '#b4381e', speed: 2.2, up: 1.6, size: 0.14, life: 16, gravity: 1 });
      } else if (e.kind === 'web') {
        this.ctx.vfx.burst(p.setY(p.y + 1.5), { n: 20, color: '#eef2f6', speed: 1.6, up: 1.4, size: 0.1, life: 20 });
      } else if (e.kind === 'glade') {
        this.ctx.flare(p.setY(p.y + 1.2), '#fff2e8', 4, 20);
      }
    }
  }

  draw(s: BattleState): void {
    this.lastState = s;
    if (this.Ll) this.drawWood(s);
    if (this.Pl) this.drawPast(s);
    if (this.Ul) this.drawRoots(s);
    this.drawRings(s);
  }

  /** The rings: a stalker heaving by Ceda (red, closing), the next root's top before the rot comes down it, the broodmother's leap, a web's time. */
  private drawRings(s: BattleState): void {
    const rings = this.rings;
    const w = s.senda?.autumn;
    const r = s.rules?.senda?.autumn;
    if (!rings || !w || !r) return;
    let q = 0;
    const ring = (x: number, z: number, y: number, rad: number, color: THREE.Color): void => {
      if (q >= rings.instanceMatrix.count) return;
      this.m4.compose(this.v3.set(x, y, z), this.q4.identity(), this.s3.set(rad, 1, rad));
      rings.setMatrixAt(q, this.m4);
      rings.setColorAt(q++, color);
    };
    if (r.leaves)
      for (const id of Object.keys(w.sk)) {
        const m = w.sk[Number(id)] as number[];
        if (m[0] !== 1) continue;
        const e = s.enemies.find((x) => x.id === Number(id));
        if (!e) continue;
        const k = Math.max(0, Math.min(1, ((m[1] as number) - s.tick) / Math.max(1, r.leaves.stalk.warn)));
        const p = this.ctx.at(e.x, e.y, 0.08);
        ring(p.x, p.z, p.y, 0.9, WARN);
        ring(p.x, p.z, p.y, 0.25 + 0.65 * k, WARN2);
      }
    if (r.blight) {
      const nx = r.blight.waves[w.wave];
      if (nx && nx.t - s.waveT <= 36 && nx.t - s.waveT >= 0) {
        const p = this.nodeAt[r.blight.srcs[nx.src] as number];
        const k = (nx.t - s.waveT) / 36;
        if (p) {
          ring(p.x, p.z, p.y + 0.05, 1.1, WARN);
          ring(p.x, p.z, p.y + 0.05, 0.25 + 0.85 * k, WARN2);
        }
      }
      // Each head's next cell.
      for (const hd of w.heads) {
        const p = this.nodeAt[hd.i];
        if (p) ring(p.x, p.z, p.y + 0.04, 0.62, WHITE);
      }
    }
    if (r.under) {
      if (w.bt >= 0) {
        const p = this.ctx.at(w.bx, w.by, 0.08);
        const k = Math.max(0, Math.min(1, (w.bt - s.tick) / Math.max(1, r.under.boss.warn)));
        const R = (r.under.boss.r / 1000) * S;
        ring(p.x, p.z, p.y, R, WARN);
        ring(p.x, p.z, p.y, R * (0.15 + 0.85 * k), WARN2);
      }
      for (const id of Object.keys(w.webs)) {
        const c = s.enemies.find((x) => x.id === Number(id));
        if (!c) continue;
        const left = Math.max(0, Math.min(1, ((w.webs[Number(id)] as number) - s.tick) / Math.max(1, r.under.web.cut)));
        const p = this.ctx.at(c.x, c.y, 0.08);
        ring(p.x, p.z, p.y, 0.9, WARN);
        ring(p.x, p.z, p.y, 0.2 + 0.7 * left, WARN2);
      }
    }
    rings.count = q;
    rings.instanceMatrix.needsUpdate = true;
    if (rings.instanceColor) rings.instanceColor.needsUpdate = true;
  }

  frame(now: number, camera?: THREE.Camera): void {
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    this.dome?.frame(now, camera);
    if (camera) {
      lookPoint(camera, this.look);
      this.leavesA?.frame(now, this.look);
      this.leavesB?.frame(now, this.look);
    }
    this.seedFlash = Math.max(0, this.seedFlash - dt * 0.35);
    if (this.outT >= 0 && this.outT < 99) {
      this.outT += dt;
      if (this.Ul) this.drawRoots(this.lastState);
    }
    // The boughs lost: each falls away and down, its leaves going grey.
    this.boughs.forEach((b, i) => {
      const f = this.fall[i] as number;
      if (f <= 0) return;
      const nf = Math.min(1, f + dt * 0.6);
      this.fall[i] = nf;
      const dir = i === 0 ? 1 : i === 2 ? -1 : 0.4;
      b.rotation.z = dir * nf * 1.4;
      b.position.y = (this.mapleAt?.y ?? 0) + 4.4 - nf * nf * 3.6;
      b.position.x = (this.mapleAt?.x ?? 0) - dir * nf * 1.6;
    });
    // The pulse of the heads (the memory), redrawn between ticks.
    if (this.Pl && this.lastState) this.drawPast(this.lastState);
    this.glow.begin();
    for (const l of this.lights) {
      const f = l.flick ? 0.82 + 0.18 * Math.sin(now * (3 + l.seed) + l.seed) * l.flick : 1;
      this.glow.add(l.p.x, l.p.y, l.p.z, l.c, l.size, l.a * f);
    }
    const s = this.lastState;
    const w = s?.senda?.autumn;
    // The memory's heads glow pale (they read in its grey); the cut ends green.
    if (this.Pl && w) {
      for (const hd of w.heads) {
        const p = this.nodeAt[hd.i];
        if (p) this.glow.add(p.x, p.y + 0.4, p.z, ROT_GLOW, 1.8 + 0.4 * Math.sin(now * 7 + hd.i), 0.7);
      }
      w.rot.forEach((v, i) => {
        const p = this.nodeAt[i];
        if (v === 2 && p) this.glow.add(p.x, p.y + 0.6, p.z, DRUID, 1.4, 0.55);
      });
    }
    // The test: the knot's pulse, the rot's fronts, the cuts.
    if (this.Ul && w && s?.rules?.senda?.autumn?.under) {
      const r = s.rules.senda.autumn.under;
      if (this.knotAt && w.done < 0) this.glow.add(this.knotAt.x, this.knotAt.y + 0.5, this.knotAt.z, ROT_GLOW, 2.6 + 0.6 * Math.sin(now * 4), 0.6);
      r.strands.forEach((st, k) => {
        if (!st.rot || w.done >= 0) return;
        const i = st.at[w.front[k] as number];
        const p = i !== undefined && i >= 0 ? this.cellAt[i] : undefined;
        if (p) this.glow.add(p.x, p.y + 0.3, p.z, ROT_GLOW, 1.6 + 0.3 * Math.sin(now * 6 + k), 0.65);
      });
      w.cut.forEach((v, i) => {
        const p = this.cellAt[i];
        if (v && p) this.glow.add(p.x + 0.42, p.y + 0.9, p.z + 0.3, DRUID, 1.1, 0.55);
      });
    }
    if (this.seedFlash > 0 && this.mapleAt) this.glow.add(this.mapleAt.x, this.mapleAt.y + 4, this.mapleAt.z + 0.8, WHITE, 3 + this.seedFlash * 6, this.seedFlash);
    this.glow.end();
    void this.hour;
  }

  /** The memory's root cells and the test's cells with roots, in world points (the HUD's cut offer). */
  cutPoints(): THREE.Vector3[] {
    if (this.Pl) return this.nodeAt.map((p) => p.clone().setY(p.y + 0.3));
    if (this.Ul) return this.cellAt.map((p) => p.clone().setY(p.y + 0.3));
    return [];
  }

  dispose(): void {
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    this.dome?.dispose();
    this.leavesA?.dispose();
    this.leavesB?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}
